/**
 * Utility functions for managing recent searches
 */

const RECENT_SEARCHES_KEY = "pulpit_recent_searches"
const MAX_RECENT_SEARCHES = 5

/**
 * Get recent searches from localStorage
 * @returns {Array} Array of recent search objects
 */
export const getRecentSearches = () => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    if (!stored) return []
    const searches = JSON.parse(stored)
    return Array.isArray(searches) ? searches.slice(0, MAX_RECENT_SEARCHES) : []
  } catch (error) {
    console.error("Error reading recent searches:", error)
    return []
  }
}

/**
 * Save a search to recent searches
 * @param {Object} searchOption - The search option object with label and value
 * @param {string} field - 'from' or 'to'
 */
export const saveRecentSearch = (searchOption, field) => {
  try {
    if (!searchOption || !searchOption.label) return

    const searches = getRecentSearches()
    const searchKey = `${field}_${searchOption.label}`
    
    // Remove if already exists
    const filtered = searches.filter(
      (s) => `${s.field}_${s.label}` !== searchKey
    )
    
    // Add to beginning
    filtered.unshift({
      ...searchOption,
      field,
      timestamp: Date.now(),
    })
    
    // Keep only MAX_RECENT_SEARCHES
    const limited = filtered.slice(0, MAX_RECENT_SEARCHES)
    
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(limited))
  } catch (error) {
    console.error("Error saving recent search:", error)
  }
}

/**
 * Get recent searches for a specific field
 * @param {string} field - 'from' or 'to'
 * @returns {Array} Array of recent search options
 */
export const getRecentSearchesForField = (field) => {
  const searches = getRecentSearches()
  return searches
    .filter((s) => s.field === field)
    .map(({ field: _, timestamp: __, ...option }) => option)
    .slice(0, 5) // Max 5 recent searches
}

// ─── Recent Trips (full trip objects) ─────────────────────────────────────────

const RECENT_TRIPS_KEY = "pulpit_recent_trips"
const MAX_RECENT_TRIPS = 2

/**
 * Save a complete trip to recent trips
 * @param {{ trip_type: number, fromCity: string, toCity?: string, package?: string }} trip
 */
export const saveRecentTrip = (trip) => {
  try {
    if (!trip?.trip_type || !trip?.fromCity) return
    const trips = getRecentTrips()
    // dedup key
    const key =
      trip.trip_type === 2
        ? `2_${trip.fromCity}`
        : `${trip.trip_type}_${trip.fromCity}_${trip.toCity}`
    const filtered = trips.filter((t) => {
      const k =
        t.trip_type === 2
          ? `2_${t.fromCity}`
          : `${t.trip_type}_${t.fromCity}_${t.toCity}`
      return k !== key
    })
    filtered.unshift({ ...trip, timestamp: Date.now() })
    localStorage.setItem(
      RECENT_TRIPS_KEY,
      JSON.stringify(filtered.slice(0, MAX_RECENT_TRIPS))
    )
  } catch {}
}

/**
 * Get the last N complete trip searches
 * @returns {Array}
 */
export const getRecentTrips = () => {
  try {
    const stored = localStorage.getItem(RECENT_TRIPS_KEY)
    if (!stored) return []
    const trips = JSON.parse(stored)
    return Array.isArray(trips) ? trips.slice(0, MAX_RECENT_TRIPS) : []
  } catch {
    return []
  }
}

